interface WorkflowExample {
  num: string;
  title: string;
  blurb: string;
  /** Still stand-in content — renders the "example incoming" label. */
  placeholder?: boolean;
}

const EXAMPLES: WorkflowExample[] = [
  {
    num: "01",
    title: "A hiring pipeline for agents",
    blurb: "New skills and agents don't get added — they get hired. Intake, sourcing, drafted candidates, automated interviews against a rubric, side-by-side comparison on real test scenarios, a trial run, and a security audit before anything is cleared. Everything is end-to-end tested and validated, and nothing goes live without passing.",
  },
  {
    num: "02",
    title: "Engineering pipeline",
    blurb: "Design, decompose, implement, review — chained, with test-driven discipline throughout and a review loop that sends failing work back to implementation until it's green. Parallel implementers work in isolated worktrees, and every finding is adversarially verified before it counts.",
  },
  {
    num: "03",
    title: "Investigation pipeline",
    blurb: "Multi-agent research that fans out across sources, then turns on itself: an independent auditor checks every claim against its cited source and scores how much each source can be trusted. You get a cited report with the weak spots flagged, not a confident-sounding guess.",
  },
  {
    num: "04",
    title: "Learning pipeline",
    blurb: "Point it at a codebase, a pull request, or any concept and it builds a personalized lesson — every claim verified against the real source by a separate fact-checker, plus interactive walkthroughs and a quiz that records what you've actually mastered.",
  },
];

export function Workflows() {
  return (
    <section id="workflows" style={{ position: "relative", padding: "var(--section-y, 120px) 32px" }}>
      <div className="container">
        <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 760, marginBottom: 56 }}>
          <span className="rule-eyebrow">Agentic workflows</span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(36px, 5vw, 60px)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
            }}
          >
            Super-charging agentic workflows.
          </h2>
          <p className="lead" style={{ maxWidth: 640 }}>
            Not one assistant — a whole org of them, with the pipelines to keep them honest. These
            run my own practice today, and the same patterns transfer to your work:
          </p>
        </div>

        <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 20 }}>
          {EXAMPLES.map((example) => (
            <article
              key={example.num}
              className="card"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--hairline)",
                borderRadius: "var(--r-lg)",
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="num">— {example.num}</span>
                {example.placeholder && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: "var(--ink-5)",
                    }}
                  >
                    · Placeholder
                  </span>
                )}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 22,
                  lineHeight: 1.2,
                  letterSpacing: "-0.01em",
                  color: "var(--ink)",
                }}
              >
                {example.title}
              </h3>
              <p style={{ margin: 0, fontFamily: "var(--font-body)", fontSize: 14, lineHeight: 1.6, color: "var(--ink-2)" }}>
                {example.blurb}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
